import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MdHomeWork, MdQuiz } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import PdfPreviewModal from "../../../components/student/PdfPreviewModal";
import SolveHomework from "../../../containers/student/models/solve-homework";
import { studentPalette } from "../../../constants/studentPalette";
import { useAdminTheme } from "../../../context/AdminThemeContext";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { getChapterContentRequest } from "../../../modules/students-modules/chapters/Actions";
import { getAllSectionsRequest } from "../../../modules/students-modules/sections/Actions";
import { getSection } from "../../../modules/students-modules/sections/Api";
import {
  ChapterContentGrid,
  CourseStatsGrid,
  getHeroStyles,
} from "../courses/course-details";

const C = {
  ...studentPalette,
  border: studentSurfaces.border,
  muted: studentSurfaces.textMuted,
};

const TAB_HOMEWORK = "homework";
const TAB_EXAMS = "exams";

const StudentQuickLinks = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { themeMode } = useAdminTheme();
  const hero = useMemo(() => getHeroStyles(themeMode === "dark"), [themeMode]);

  const sectionsRaw = useSelector((s) => s.sections.sections);
  const sectionsLoading = useSelector((s) => s.sections.isLoading);
  const chapterContentById = useSelector((s) => s.studentChapters.chapterContentById);
  const userData = useSelector((s) => s.user.userData);

  const activeTab = searchParams.get("tab") === TAB_EXAMS ? TAB_EXAMS : TAB_HOMEWORK;
  const contentFilter = activeTab === TAB_HOMEWORK ? "homework" : "exams";

  const [chaptersBySection, setChaptersBySection] = useState({});
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [openBySection, setOpenBySection] = useState({});
  const [pdfPreview, setPdfPreview] = useState(null);

  const sections = useMemo(() => {
    const raw = sectionsRaw;
    return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  }, [sectionsRaw]);

  const stats = useMemo(() => {
    let homeworksCount = 0;
    let examsCount = 0;
    let pendingHomeworksCount = 0;
    let pendingExamsCount = 0;

    sections.forEach((section) => {
      homeworksCount += Number(section?.homeworks_count) || 0;
      examsCount += Number(section?.exams_count) || 0;
      pendingHomeworksCount +=
        Number(section?.pending_homeworks_count ?? section?.remaining_homeworks_count) || 0;
      pendingExamsCount +=
        Number(section?.pending_exams_count ?? section?.remaining_exams_count) || 0;
    });

    return { homeworksCount, examsCount, pendingHomeworksCount, pendingExamsCount };
  }, [sections]);

  useEffect(() => {
    dispatch(getAllSectionsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!sections.length) return undefined;

    let cancelled = false;
    setChaptersLoading(true);

    Promise.all(
      sections.map(async (section) => {
        if (!section?.id) return { sectionId: null, chapters: [] };
        try {
          const res = await getSection(section.id);
          const data = res?.data?.data ?? res?.data ?? [];
          return {
            sectionId: section.id,
            chapters: Array.isArray(data) ? data : [],
          };
        } catch {
          return { sectionId: section.id, chapters: [] };
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach(({ sectionId, chapters }) => {
        if (sectionId != null) next[sectionId] = chapters;
      });
      setChaptersBySection(next);
      setChaptersLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [sections]);

  const ensureContent = useCallback(
    (sectionId, indices) => {
      indices.forEach((chapterIndex) => {
        const chapter = chaptersBySection[sectionId]?.[chapterIndex];
        if (!chapter?.id) return;
        const bucket = chapterContentById[chapter.id];
        if (!bucket?.loaded && !bucket?.loading) {
          dispatch(getChapterContentRequest(chapter.id));
        }
      });
    },
    [chaptersBySection, chapterContentById, dispatch]
  );

  const handleSectionAccordionChange = (sectionId) => (expandedIndex) => {
    const next = Array.isArray(expandedIndex)
      ? expandedIndex
      : expandedIndex === -1
        ? []
        : [expandedIndex];
    setOpenBySection((prev) => ({ ...prev, [sectionId]: next }));
    ensureContent(sectionId, next);
  };

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
    setOpenBySection({});
  };

  const tabButtonProps = (tab) => ({
    variant: "unstyled",
    px: { base: 5, md: 8 },
    py: 3,
    fontWeight: "800",
    fontSize: "sm",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: activeTab === tab ? C.primary : C.muted,
    borderBottomWidth: "3px",
    borderColor: activeTab === tab ? C.primary : "transparent",
    borderRadius: 0,
    _hover: { color: C.primary },
  });

  const quickLinksReturnPath = `/students/quick-links?tab=${activeTab}`;

  return (
    <StudentPortalShell>
      <Flex direction="column" minH={{ md: "100vh" }}>
        <Box
          bg={hero.bg}
          w="100%"
          py={{ base: 10, md: 16 }}
          px={{ base: 4, md: 12 }}
          borderBottomWidth="1px"
          borderColor={hero.border}
        >
          <Box maxW="5xl" mx="auto" w="100%" color={hero.text}>
            <Text
              fontSize="xs"
              fontWeight="800"
              letterSpacing="0.2em"
              textTransform="uppercase"
              color={hero.textMuted}
              mb={2}
            >
              Quick access
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="800"
              color={hero.text}
              textTransform="uppercase"
              letterSpacing="tight"
              lineHeight="1.15"
              mb={6}
            >
              Quick Links
            </Heading>

            <CourseStatsGrid
              sessionsCount={0}
              liveCount={0}
              pdfsCount={0}
              examsCount={stats.examsCount}
              homeworksCount={stats.homeworksCount}
              pendingExamsCount={stats.pendingExamsCount}
              pendingHomeworksCount={stats.pendingHomeworksCount}
              hero={hero}
            />
          </Box>
        </Box>

        <Box maxW="4xl" mx="auto" py={{ base: 10, md: 16 }} px={{ base: 4, md: 6 }} w="100%">
          <Flex
            align="center"
            gap={2}
            borderBottomWidth="2px"
            borderColor={C.border}
            mb={10}
            flexWrap="wrap"
          >
            <Button {...tabButtonProps(TAB_HOMEWORK)} onClick={() => setActiveTab(TAB_HOMEWORK)} leftIcon={<MdHomeWork size={18} />}>
              Homework
            </Button>
            <Button {...tabButtonProps(TAB_EXAMS)} onClick={() => setActiveTab(TAB_EXAMS)} leftIcon={<MdQuiz size={18} />}>
              Exams
            </Button>
          </Flex>

          <Flex align="center" justify="space-between" borderBottomWidth="2px" borderColor={C.primary} pb={4} mb={10} flexWrap="wrap" gap={4}>
            <Heading fontSize="2xl" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="tight">
              {activeTab === TAB_HOMEWORK ? "Homework & classified" : "Exams"}
            </Heading>
            <Text fontSize="sm" fontWeight="600" color={C.muted}>
              {activeTab === TAB_HOMEWORK
                ? "Homework assignments and classified exams (type 2)"
                : "All other exam types across your courses"}
            </Text>
          </Flex>

          {sectionsLoading || (chaptersLoading && sections.length > 0) ? (
            <VStack spacing={4} align="stretch">
              {[1, 2, 3].map((k) => (
                <Skeleton key={k} h="72px" borderRadius="xl" />
              ))}
            </VStack>
          ) : sections.length === 0 ? (
            <Text color={C.muted} fontWeight="600">
              No courses are available yet.
            </Text>
          ) : (
            <VStack spacing={10} align="stretch">
              {sections.map((section) => {
                const chapters = chaptersBySection[section.id] || [];
                if (chapters.length === 0) return null;

                return (
                  <Box key={section.id}>
                    <Heading
                      fontSize="lg"
                      fontWeight="800"
                      color={C.primary}
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      mb={4}
                    >
                      {section.name || "Course"}
                    </Heading>

                    <Accordion
                      allowMultiple
                      index={openBySection[section.id] || []}
                      onChange={handleSectionAccordionChange(section.id)}
                    >
                      {chapters.map((chapter, index) => {
                        const bucket = chapterContentById[chapter.id];

                        return (
                          <AccordionItem
                            key={chapter.id}
                            bg={studentSurfaces.raised}
                            borderWidth="1px"
                            borderColor={C.border}
                            borderRadius="xl"
                            mb={4}
                            overflow="hidden"
                          >
                            <AccordionButton
                              px={6}
                              py={5}
                              _hover={{ bg: studentSurfaces.muted }}
                              borderBottomWidth="1px"
                              borderColor={C.border}
                            >
                              <Flex flex={1} align="center" gap={4} textAlign="left">
                                <Text fontWeight="800" fontSize="2xl" color={C.primary} opacity={0.2} minW="36px">
                                  {String(index + 1).padStart(2, "0")}
                                </Text>
                                <Text flex={1} fontWeight="700" fontSize="md" color={C.primary}>
                                  {chapter.name || `Lesson ${index + 1}`}
                                </Text>
                                {bucket?.loading ? <Spinner size="sm" color={C.primary} /> : null}
                              </Flex>
                              <AccordionIcon color={C.primary} />
                            </AccordionButton>
                            <AccordionPanel p={0}>
                              <ChapterContentGrid
                                bucket={bucket}
                                chapter={chapter}
                                userData={userData}
                                courseReturnPath={quickLinksReturnPath}
                                onOpenPdf={setPdfPreview}
                                contentFilter={contentFilter}
                              />
                            </AccordionPanel>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      </Flex>

      <PdfPreviewModal
        isOpen={Boolean(pdfPreview?.url)}
        onClose={() => setPdfPreview(null)}
        pdfUrl={pdfPreview?.url || ""}
        title={pdfPreview?.title}
        backLabel={pdfPreview?.backLabel}
        totalPages={pdfPreview?.totalPages}
      />
    </StudentPortalShell>
  );
};

export default StudentQuickLinks;
